import { useEffect, useMemo, useRef } from "react";
import { ProgramPhase } from "@core/features/program";
import { useSelectedPhase } from "@platform/store";
import { ProgramPhasesTimelineContainer } from "../../components/PhasesTimeline";
import { ProgramPhaseListContainer } from "../../components/PhaseList";
import { scrollTimelineToPhase } from "@platform/features/programPhases";

type Props = {
  currentPhase: ProgramPhase | undefined;
  phases: ProgramPhase[];
  isLoading: boolean;
  programId: number;
};

export function ProgramPhases({
  currentPhase,
  phases,
  isLoading,
  programId,
}: Props) {
  const timelineRef = useRef<HTMLDivElement>(null);

  const { id: selectedPhaseId, setId: setSelectedPhaseId } = useSelectedPhase();

  const selectedPhase = useMemo(() => {
    if (!selectedPhaseId) return undefined;
    return phases.find((phase) => phase.id === selectedPhaseId);
  }, [selectedPhaseId, phases]);

  // Initialize selection to the active phase once phases are loaded.
  useEffect(() => {
    if (selectedPhaseId) return;
    if (!currentPhase) return;
    setSelectedPhaseId(currentPhase.id);
  }, [currentPhase, selectedPhaseId, setSelectedPhaseId]);

  const handlePhaseChange = (phase: ProgramPhase) => {
    setSelectedPhaseId(phase.id);
    scrollTimelineToPhase({
      index: phases.findIndex((p) => p.id === phase.id),
      timelineRef,
    });
  };

  // Reset selected phase when component unmounts
  useEffect(() => {
    return () => {
      setSelectedPhaseId(null);
    };
  }, [setSelectedPhaseId]);

  return (
    <>
      <div className="flex flex-col gap-8 py-4 w-full">
        <ProgramPhasesTimelineContainer
          timelineRef={timelineRef}
          phases={phases}
          selectedPhase={selectedPhase}
          setSelectedPhase={handlePhaseChange}
        />
      </div>

      <ProgramPhaseListContainer
        selectedPhase={selectedPhase}
        setSelectedPhase={handlePhaseChange}
        isLoading={isLoading}
        phases={phases}
        programId={programId}
      />
    </>
  );
}

export default ProgramPhases;
